import { TicketStatusColor } from "@lytra/frontend-common/__generated__/graphql";

/**
 * A ticket status's colour (ADR-0078): the one place a palette key becomes classes. The
 * swatches come from DESIGN.md's brand scales and the semantic tokens; tokens flip with
 * the theme, the few raw brand stops carry their own dark variant. Every chip pairs a
 * tint with a darker text of the same hue, at 5.3:1 or better in both themes.
 *
 * Listed in full so Tailwind's scan sees every class.
 */
export interface StatusColorClasses {
  /** The swatch's German name, for the colour picker's buttons. */
  label: string;
  /** Status dot, colour swatch. */
  dot: string;
  /** Chip: tinted background and text. */
  chip: string;
  /** Board column bar. */
  bar: string;
}

export const STATUS_COLORS: Record<TicketStatusColor, StatusColorClasses> = {
  [TicketStatusColor.Blue]: {
    label: "Blau",
    dot: "bg-primary",
    chip: "bg-primary-subtle text-primary-strong",
    bar: "border-t-primary",
  },
  [TicketStatusColor.Indigo]: {
    label: "Indigo",
    dot: "bg-primary-strong",
    chip: "bg-primary-line text-primary-strong",
    bar: "border-t-primary-strong",
  },
  [TicketStatusColor.Navy]: {
    label: "Marineblau",
    dot: "bg-secondary",
    chip: "bg-muted text-ink",
    bar: "border-t-secondary",
  },
  [TicketStatusColor.Slate]: {
    label: "Grau",
    dot: "bg-ink-faint",
    chip: "bg-muted text-ink-muted",
    bar: "border-t-ink-faint",
  },
  [TicketStatusColor.Cyan]: {
    label: "Türkis",
    dot: "bg-info-muted",
    chip: "bg-info-subtle text-info-strong",
    bar: "border-t-info-muted",
  },
  [TicketStatusColor.Purple]: {
    label: "Violett",
    dot: "bg-accent",
    chip: "bg-ai-subtle text-accent-strong",
    bar: "border-t-accent",
  },
  [TicketStatusColor.Plum]: {
    label: "Pflaume",
    dot: "bg-accent-strong",
    // Purple 2 / Purple 10; at night Purple 7 / Purple 0.5.
    chip: "bg-[#EEC6F5] text-[#230727] dark:bg-[#8E4799] dark:text-[#FBF1FC]",
    bar: "border-t-accent-strong",
  },
  [TicketStatusColor.Yellow]: {
    label: "Gelb",
    // Yellow 7: Yellow 5 alone vanishes on white.
    dot: "bg-[#B3B552]",
    chip: "bg-warning-fill text-warning-strong dark:bg-warning-subtle",
    bar: "border-t-[#B3B552]",
  },
  [TicketStatusColor.Olive]: {
    label: "Oliv",
    dot: "bg-warning-strong",
    // Yellow 2 / Yellow 10; at night the other way round.
    chip: "bg-[#FBFCCF] text-warning-strong dark:bg-[#4F5105] dark:text-[#FBFCCF]",
    bar: "border-t-warning-strong",
  },
  [TicketStatusColor.Green]: {
    label: "Grün",
    dot: "bg-success",
    chip: "bg-success-subtle text-success-strong",
    bar: "border-t-success",
  },
  [TicketStatusColor.Red]: {
    label: "Rot",
    dot: "bg-destructive",
    chip: "bg-destructive-subtle text-destructive-strong",
    bar: "border-t-destructive",
  },
};

/** The palette in picker order, hue by hue (the generated enum is alphabetical). The
 * server hands a new status the first colour no status has, in the same order. */
export const STATUS_PALETTE: readonly TicketStatusColor[] = [
  TicketStatusColor.Blue,
  TicketStatusColor.Indigo,
  TicketStatusColor.Navy,
  TicketStatusColor.Slate,
  TicketStatusColor.Cyan,
  TicketStatusColor.Purple,
  TicketStatusColor.Plum,
  TicketStatusColor.Yellow,
  TicketStatusColor.Olive,
  TicketStatusColor.Green,
  TicketStatusColor.Red,
];

/** A status's classes; a status not loaded yet reads neutral. */
export const statusColorClasses = (
  color: TicketStatusColor | null | undefined,
): StatusColorClasses => STATUS_COLORS[color ?? TicketStatusColor.Slate];
